import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Alert } from "react-bootstrap";

const statusCopy = {
  hindi: {
    languageToggleLabel: 'भाषा',
    title: 'आपकी वर्तमान स्थिति',
    subtitle: 'जिन परीक्षाओं को आपने पहले ही पास कर लिया है, उन्हें चुनें',
    loading: 'लोड हो रहा है...',
    continue: 'जारी रखें',
    saving: 'सहेजा जा रहा है...',
    skip: 'अभी के लिए छोड़ें',
    warning:
      'यदि आपने अभी तक कोई परीक्षा पास नहीं की है तो चिंता न करें। हम आपके लिए एक व्यक्तिगत सीखने का मार्ग बनाएंगे।',
    warningTitle: 'ध्यान दें',
    selectAtLeastOne: 'कृपया कम से कम एक स्थिति चुनें या अभी के लिए छोड़ें पर टैप करें।',
    loadFailed: 'वर्तमान स्थिति विकल्प लोड करने में विफल रहा।',
    saveFailed: 'वर्तमान स्थिति सहेजने में विफल रहा।',
    iconLabel: 'सत्यापित स्थिति',
  },
  english: {
    languageToggleLabel: 'Language',
    title: 'Your Current Status',
    subtitle: 'Select any exams you have already passed',
    loading: 'Loading...',
    continue: 'Continue',
    saving: 'Saving...',
    skip: 'Skip for now',
    warning:
      "Don't worry if you haven't passed any exams yet. We'll create a personalized learning path just for you.",
    warningTitle: 'Note',
    selectAtLeastOne: 'Please select at least one status or tap Skip for now.',
    loadFailed: 'Failed to load current status options.',
    saveFailed: 'Failed to save current status.',
    iconLabel: 'Verified status',
  },
};

function CurrentStatus() {
  const navigate = useNavigate();
  const authToken = localStorage.getItem("AuthToken");
  const BASE_URL = "https://api.japaking.online";

  const [language, setLanguage] = useState(() => localStorage.getItem("statusLanguage") || "hindi");
  const [examPassTypes, setExamPassTypes] = useState([]);
  const [selectedExamPassIds, setSelectedExamPassIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const text = statusCopy[language] || statusCopy.hindi;

  useEffect(() => {
    localStorage.setItem("statusLanguage", language);
  }, [language]);

  useEffect(() => {
    if (!authToken) {
      navigate("/login");
      return;
    }

    const fetchExamPassTypes = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const response = await fetch(`${BASE_URL}/api/current-exam-pass-types`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        const data = await response.json();

        if (!response.ok || !data?.success) {
          throw new Error(data?.message || text.loadFailed);
        }

        setExamPassTypes(data?.message?.currentExamPassTypes || []);
      } catch (error) {
        setErrorMessage(error?.message || text.loadFailed);
      } finally {
        setLoading(false);
      }
    };

    fetchExamPassTypes();
  }, [BASE_URL, authToken, navigate, text.loadFailed]);

  const handleContinue = async () => {
    if (!selectedExamPassIds.length) {
      setErrorMessage(text.selectAtLeastOne);
      return;
    }

    try {
      setSaving(true);
      setErrorMessage("");

      const response = await fetch(`${BASE_URL}/api/users/profile-fields`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          current_exam_pass_id: selectedExamPassIds,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || text.saveFailed);
      }

      navigate("/");
    } catch (error) {
      setErrorMessage(error?.message || text.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate("/");
  };

  const handleToggleExamPass = (examPassId) => {
    setSelectedExamPassIds((prev) => {
      if (prev.includes(examPassId)) {
        return prev.filter((id) => id !== examPassId);
      }
      return [...prev, examPassId];
    });
  };

  return (
    <div className="status-v2-page">
      <div className="status-v2-card">
        <div className="login-v2-language-toggle" role="tablist" aria-label={text.languageToggleLabel}>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'hindi' ? 'active' : ''}`}
            onClick={() => setLanguage('hindi')}
          >
            हिंदी
          </button>
          <button
            type="button"
            className={`login-v2-language-option ${language === 'english' ? 'active' : ''}`}
            onClick={() => setLanguage('english')}
          >
            English
          </button>
        </div>

        <div className="status-v2-info-box status-v2-info-box-top">
          <span className="status-v2-info-icon">!</span>
          <p>
            <strong>{text.warningTitle}: </strong>
            {text.warning}
          </p>
        </div>

        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}
        <div className="status-v2-icon-box">
          <CheckCircleOutlineIcon className="status-v2-icon" aria-label={text.iconLabel} />
        </div>

        <h1 className="status-v2-title">{text.title}</h1>
        <p className="status-v2-subtitle">{text.subtitle}</p>

        {loading ? (
          <p className="status-v2-loading">{text.loading}</p>
        ) : (
          <div className="status-v2-options">
            {examPassTypes.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`status-v2-option ${selectedExamPassIds.includes(item.id) ? "active" : ""}`}
                onClick={() => handleToggleExamPass(item.id)}
              >
                <input 
                  type="checkbox" 
                  checked={selectedExamPassIds.includes(item.id)} 
                  readOnly 
                  style={{ width: "20px", height: "20px", marginRight: "15px", pointerEvents: "none", flexShrink: 0, accentColor: "#ffc0cb" }}
                />
                <span className="status-v2-option-text">
                  <span className="status-v2-option-title">{item.exam_pass_name}</span>
                  <span className="status-v2-option-desc">{item.exam_pass_desc}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          className="status-v2-continue"
          onClick={handleContinue}
          disabled={saving || loading || !examPassTypes.length}
        >
          {saving ? text.saving : text.continue}
        </button>

        <button className="status-v2-skip" onClick={handleSkip} disabled={saving}>
          {text.skip}
        </button>
      </div>
    </div>
  );
}

export default CurrentStatus;
